import {
  Box,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
  Flex,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';

const TermsPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize={'15px'}
      lineHeight={'24px'}>
      <VStack align="start" spacing={16}>
        <Box>
          <Heading as="h1" fontSize={'40px'} mb={4} fontFamily="marketingPage">
            Terms of Service
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Last updated: July 2025
          </Text>
          <Text mt={4}>
            Welcome to Merkatus.io. By accessing or using our website or SaaS
            platform (&quot;Service&quot;), you agree to the following Terms of
            Service (&quot;Terms&quot;). Please read them carefully.
          </Text>
        </Box>

        {/* Section 1 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            1. Who We Are
          </Heading>
          <Text>
            Merkatus.io is operated by <strong>Merkatus.io Oy</strong>, a
            Finnish company offering AI-powered marketing mix modeling tools
            designed for clarity, speed, and local market precision.
          </Text>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            2. Eligibility & Accounts
          </Heading>
          <Text mb={1}>To use the platform, you must:</Text>
          <UnorderedList pl={4} spacing={1}>
            <ListItem>Be at least 18 years old</ListItem>
            <ListItem>Be authorized to represent your company</ListItem>
            <ListItem>
              Provide accurate registration and billing information
            </ListItem>
          </UnorderedList>
          <Text mt={1}>
            You are responsible for keeping your account credentials secure and
            for all actions taken under your account.
          </Text>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            3. Use of the Service
          </Heading>
          <Text mb={1}>You agree to:</Text>
          <UnorderedList pl={4} spacing={1}>
            <ListItem>
              Use the platform solely for legal business purposes
            </ListItem>
            <ListItem>
              Avoid reverse-engineering, tampering, or unauthorized access
            </ListItem>
            <ListItem>
              Refrain from uploading harmful or unlawful content
            </ListItem>
            <ListItem>Respect intellectual property rights</ListItem>
          </UnorderedList>
          <Text mt={1}>
            Violation may lead to suspension or termination of your access.
          </Text>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            4. Data & Privacy Compliance
          </Heading>
          <Text>
            Merkatus does{' '}
            <strong>
              not use any personal data or personally identifiable information
              (PII)
            </strong>{' '}
            in our modeling. All modeling is based on anonymized, aggregated
            business data only. As a result, our platform is{' '}
            <strong>natively GDPR-compliant</strong>, minimizing legal risk and
            simplifying data handling. Use of the platform is also governed by
            our{' '}
            <Link
              href="/privacy-policy"
              color="darkBlue.900"
              textDecoration="underline">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              href="/cookie-policy"
              color="darkBlue.900"
              textDecoration="underline">
              Cookie Policy
            </Link>
            .
          </Text>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            5. Subscription & Payment
          </Heading>
          <UnorderedList pl={4} spacing={1}>
            <ListItem>Pricing is listed in EUR and excludes VAT</ListItem>
            <ListItem>Billing occurs monthly or annually as selected</ListItem>
            <ListItem>
              Payments are processed securely by a trusted third-party provider
            </ListItem>
            <ListItem>
              Subscriptions renew automatically unless canceled before the next
              billing cycle
            </ListItem>
            <ListItem>
              You may cancel at any time, with access determination taking
              effect at the end of then-current Subscription period
            </ListItem>
            <ListItem>
              No refunds will be issued for partial billing periods unless
              otherwise agreed
            </ListItem>
          </UnorderedList>
          <Text mt={1}>
            Custom pricing and invoicing may apply for enterprise customers.
          </Text>
        </Box>

        {/* Section 6 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            6. Intellectual Property
          </Heading>
          <Text>
            All rights to the platform, underlying models, algorithms, code, and
            visual materials are owned by Merkatus.io Oy.
            <br />
            You may not copy, modify, distribute, or resell any part of the
            Service without prior written consent.
          </Text>
        </Box>

        {/* Section 7 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            7. Service Availability
          </Heading>
          <Text>
            We aim to provide uninterrupted access but cannot guarantee 100%
            uptime. Planned maintenance and rare outages may occur.
            <br />
            In such cases, we will make commercially reasonable efforts to
            restore service promptly.
          </Text>
        </Box>

        {/* Section 8 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            8. Limitation of Liability
          </Heading>
          <Text mb={2}>
            To the extent permitted by law, Merkatus.io Oy is not liable for:
          </Text>
          <UnorderedList pl={4} spacing={1}>
            <ListItem>Indirect or consequential damages</ListItem>
            <ListItem>
              Loss of revenue, profits, or business opportunities
            </ListItem>
            <ListItem>
              Data errors resulting from customer-provided inputs
            </ListItem>
            <ListItem>
              Decisions, actions, or outcomes based on insights,
              recommendations, or results generated by the platform’s modeling
              and AI tools
            </ListItem>
          </UnorderedList>
          <Text mt={1}>
            All analyses, insights, and forecasts provided by Merkatus.io are
            based on statistical models and available data, and therefore should
            be used as decision-support tools, not absolute guarantees.
          </Text>
          <Text mt={1}>
            Total liability is limited to one (1) month of subscription fees
            paid by you.
          </Text>
        </Box>

        {/* Section 9 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            9. Termination of Service
          </Heading>
          <Text>
            You may terminate your subscription at any time via your account
            dashboard or by contacting us at{' '}
            <Link
              color="darkBlue.900"
              href="mailto:support@merkatus.io"
              fontWeight={'semibold'}>
              support@merkatus.io
            </Link>
            .
          </Text>
          <Text mt={1}>After cancellation:</Text>
          <UnorderedList pl={4} spacing={1}>
            <ListItem>
              Your access remains active until the end of your current{' '}
              <strong>monthly billing cycle</strong>
            </ListItem>
            <ListItem>
              You may request access to export your data within{' '}
              <strong>90 days of termination</strong>
            </ListItem>
          </UnorderedList>
          <Text mt={1}>
            Merkatus reserves the right to suspend or terminate access for
            misuse, unpaid fees, or legal obligations, with reasonable notice.
          </Text>
        </Box>

        {/* Section 10 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            10. Changes to Terms
          </Heading>
          <Text>
            We may update these Terms periodically. Changes will be communicated
            through the platform or via email.
            <br />
            Continued use of the Service after updates indicates acceptance of
            the revised Terms.
          </Text>
        </Box>

        {/* Section 11 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            11. Contact
          </Heading>
          <Text>
            For any legal or operational inquiries:
            <br />
            <Flex my={1}>
              <Icon
                iconName="mail"
                width={26}
                height={26}
                fill={'transparent'}
                mr={1}
              />
              <Link
                color="darkBlue.900"
                href="mailto:support@merkatus.io"
                fontWeight={'semibold'}>
                support@merkatus.io
              </Link>
            </Flex>
            <Flex mb={1}>
              <Icon
                iconName="location"
                width={26}
                height={26}
                fill={'transparent'}
                mr={1}
              />
              Merkatus.io Oy, Helsinki, Finland
            </Flex>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default TermsPage;
